import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/hero';

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const go = useCallback((next: number) => {
    setIndex((next + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(delta < 0 ? index + 1 : index - 1);
    }
    touchStartX.current = null;
  };

  const slide = heroSlides[index];

  return (
    <section
      className="relative w-full h-[560px] md:h-[86vh] md:min-h-[560px] overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {heroSlides.map((item, i) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
        >
          <img
            src={item.image}
            alt={`无颜之月 ${item.title} 主视觉剧照`}
            title={`无颜之月 ${item.title} 在线观看`}
            className="w-full h-full object-cover object-top"
          />
        </div>
      ))}

      <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/60 to-background-50/40" />
      <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/40 to-transparent" />

      <div className="relative h-full max-w-7xl mx-auto px-4 md:px-8 flex flex-col justify-end pb-20 md:pb-28 pt-28 md:pt-40">
        <div key={slide.id} className="max-w-2xl animate-fade-up">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-500/20 text-accent-300 text-xs font-medium tracking-wide mb-4">
            <i className="ri-fire-fill" />
            {slide.tag}
          </span>
          <h1 className="font-heading font-bold text-foreground-950 text-4xl md:text-6xl tracking-wide mb-3">
            {slide.title}
          </h1>
          <p className="text-foreground-600 text-base md:text-lg mb-7 max-w-xl leading-relaxed">
            {slide.subtitle}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <Link
              to={`/episode/${slide.episodeId}`}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-fill text-lg" />
              立即播放
            </Link>
            <Link
              to="/plot"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-background-200/70 text-foreground-900 font-medium text-sm hover:bg-background-300/70 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-book-open-line" />
              查看剧情介绍
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 md:right-8 flex items-center gap-2.5">
        {heroSlides.map((item, i) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第${i + 1}张剧照`}
            onClick={() => go(i)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-8 bg-primary-500' : 'w-3 bg-foreground-400/50 hover:bg-foreground-400'
            }`}
          />
        ))}
      </div>

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(index - 1)}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-background-50/60 backdrop-blur border border-background-300/40 items-center justify-center text-foreground-900 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(index + 1)}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-background-50/60 backdrop-blur border border-background-300/40 items-center justify-center text-foreground-900 hover:bg-primary-500 hover:text-background-50 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>
    </section>
  );
}